import { router, useSegments, type Href } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Back-button helper for accounting detail pages. When the user lands on a
 * detail page via refresh or a fresh-tab deep-link, the back stack only
 * contains synthesized parent routes (Expo Router mounts `initialRouteName`
 * before navigating to the deep-link target), so `router.back()` ends up
 * on `/` — disorienting in an accounting context. Prefer the per-association
 * accounting page when we know the associationId, else the portfolio list,
 * never `/`.
 *
 * Why a navigation flag AND `router.canGoBack()` on web, never
 * `window.history.length`:
 * - `router.canGoBack()` alone can be `true` for a deep-linked page whose
 *   nested stack Expo Router seeded with its `initialRouteName` anchor.
 * - The flag alone is not enough under the root drawer's `fullHistory` (#651):
 *   a page reached by a cross-section redirect (a guard, a legacy-URL stub)
 *   has its entry REPLACED, so the drawer history is just that page and
 *   `router.back()` would dispatch a GO_BACK nothing handles — a dead button.
 * - `window.history.length` counts entries from other sites and fresh tabs.
 *
 * The flag proves "the user navigated since mount" by observing the route
 * actually change. `useTrackInAppNavigation()` is mounted once at the app
 * root; the flag stays `false` until the route changes from its initial value.
 */
let hasNavigatedSinceMount = false;

/** Mount once at the app root (`app/_layout.tsx`). */
export const useTrackInAppNavigation = (): void => {
	const segments = useSegments();
	const key = segments.join('/');
	const initialRef = useRef<string | null>(null);
	useEffect(() => {
		if (initialRef.current === null) {
			initialRef.current = key;
			return;
		}
		if (key !== initialRef.current) hasNavigatedSinceMount = true;
	}, [key]);
};

/**
 * The generic form: go back if the user really navigated here, else land on `fallback`.
 *
 * Use this for a full-page editor reached from exactly one list — the list IS the only
 * sensible destination, so a deep-linked or refreshed editor still exits somewhere useful
 * instead of `/`.
 */
export function backOr(fallback: Href) {
	const hasBack = Platform.OS === 'web' ? hasNavigatedSinceMount && router.canGoBack() : router.canGoBack();
	if (hasBack) { router.back(); return; }
	router.replace(fallback);
}

/**
 * Back out of an inbox message or a request (#369).
 *
 * Falls back to the LABEL the reader came from when there is no real history — landing
 * on "All mail" after deep-linking into a message from an email or a bookmark loses the
 * queue the staffer was working, which is the whole point of the folder they were in.
 */
export function backOrInbox(labelSlug?: string | null) {
	const hasBack = Platform.OS === 'web' ? hasNavigatedSinceMount && router.canGoBack() : router.canGoBack();
	if (hasBack) { router.back(); return; }
	router.replace((labelSlug ? `/inbox?label=${encodeURIComponent(labelSlug)}` : '/inbox') as Href);
}

/**
 * Back out of an improvement request (#369).
 *
 * Falls back to the request LIST scoped to the same lot, not to the unfiltered top: a
 * reviewer deep-linked into a request from an email has never seen the tree, and landing
 * on "All requests" hides the very community they were sent to look at.
 */
export function backOrApplications(associationId?: string | null, accountId?: string | null) {
	const hasBack = Platform.OS === 'web' ? hasNavigatedSinceMount && router.canGoBack() : router.canGoBack();
	if (hasBack) { router.back(); return; }
	const params = new URLSearchParams();
	if (associationId) params.set('associationId', associationId);
	if (accountId) params.set('accountId', accountId);
	const query = params.toString();
	router.replace((query ? `/exterior-applications?${query}` : '/exterior-applications') as Href);
}

export function backOrAccounting(associationId?: string | null) {
	const hasBack = Platform.OS === 'web' ? hasNavigatedSinceMount && router.canGoBack() : router.canGoBack();
	if (hasBack) { router.back(); return; }
	const fallback: Href = associationId
		? (`/associations/${associationId}?tab=accounting` as Href)
		: ('/associations' as Href);
	router.replace(fallback);
}
